import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  CheckboxComponent,
  DatepickerComponent,
  EnumHelper,
  FormGroupComponent,
  InputLabelComponent,
  ModalComponent,
  ModalFooterDirective,
  NumberComponent,
  SelectComponent
} from 'mbs-ui-kit';
import { stopAfterMinutesValidator } from 'mbs-ui-shared@utils/validators';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';
import { OverdueAfterSuccessPeriod } from 'mbs-ui-shared@models/backup/schedule-models';

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-additional-modal-component',
  templateUrl: './additional-modal.component.html',
  imports: [
    CheckboxComponent,
    CommonModule,
    DatepickerComponent,
    FormGroupComponent,
    I18NextPipe,
    InputLabelComponent,
    ModalComponent,
    NumberComponent,
    ReactiveFormsModule,
    SelectComponent,
    ModalFooterDirective
  ]
})
export class NBFAdditionalModalComponent implements OnInit {
  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public form: UntypedFormGroup;
  public showOverdue = true;
  public readonly overduePeriod = EnumHelper.EnumToSelectIndexesArray(OverdueAfterSuccessPeriod);
  public readonly elementSelectors = {
    id: {
      startFromDate: 'NBF-Additional-start-from-date',
      stopAfterCheckbox: 'NBF-Additional-stop-after-checkbox',
      stopAfterHours: 'NBF-Additional-stop-after-hours',
      stopAfterMinutes: 'NBF-Additional-stop-after-minutes',
      alertOverdueBlock: 'NBF-Additional-overdue-block',
      alertOverdueCheckbox: 'NBF-Additional-overdue-checkbox',
      alertOverdueDays: 'NBF-Additional-overdue-days',
      alertOverduePeriod: 'NBF-Additional-overdue-period',
      forceMissedCheckbox: 'NBF-Additional-force-missed-checkbox',
      saveButton: 'NBF-Additional-Modal-save-button',
      cancelButton: 'NBF-Additional-Modal-cancel-button'
    }
  };

  ngOnInit() {
    this.initForm();
    this.fullData();
  }

  saveClickHandler(): void {
    if (!this.form.valid) {
      return;
    }

    this.baseModal.save({ data: this.form.getRawValue() });
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }

  stopAfterEnabledChangeHandler(event): void {
    const action = event ? 'enable' : 'disable';

    ['stopAfterHours', 'stopAfterMinutes'].forEach((name) => {
      this.form.get(name)[action]();
    });

    if (event) this.form.get('stopAfterMinutes').markAllAsTouched();
  }

  stopAfterHoursChangeHandler(event): void {
    if (typeof event === 'number' && !isNaN(event)) this.form.get('stopAfterMinutes').updateValueAndValidity();
  }

  // TODO: Test
  overdueAfterSuccessChangeHandler(event): void {
    const action = event ? 'enable' : 'disable';

    ['overdueAfterSuccessAmount', 'overdueAfterSuccessPeriod'].forEach((name) => {
      this.form.get(name)[action]();
    });
  }

  private fullData(): void {
    const data = this.baseModal?.data;
    const isManualMode = data?.isManualMode;

    this.form.reset(data);

    this.stopAfterEnabledChangeHandler(this.form.get('StopAfterEnabled').value);

    if (this.showOverdue) {
      this.overdueAfterSuccessChangeHandler(this.form.get('overdueAfterSuccessEnabled').value);
    }

    if (isManualMode) {
      ['startFromDate', 'ForceMissedSchedule'].forEach((key) => this.form.get(key).disable());
    }
  }

  private initForm() {
    this.showOverdue = this.baseModal?.data?.showOverdue ?? true;

    const controls: Record<string, FormControl> = {
      startFromDate: new FormControl(new Date()),
      StopAfterEnabled: new FormControl(false),
      stopAfterHours: new FormControl(0),
      stopAfterMinutes: new FormControl(0),
      ForceMissedSchedule: new FormControl(false)
    };

    if (this.showOverdue) {
      controls.overdueAfterSuccessEnabled = new FormControl(false);
      controls.overdueAfterSuccessAmount = new FormControl(1, Validators.min(1));
      controls.overdueAfterSuccessPeriod = new FormControl(0);
    }

    this.form = new UntypedFormGroup(controls);

    this.form.get('stopAfterMinutes').addValidators(stopAfterMinutesValidator.bind({ stepForm: this.form }, 'stopAfterHours'));
  }
}
